import { __awaiter } from "tslib";
import { Container, Service } from './container';
import axios from 'axios';
import Time from 'date-and-time';
import { Config } from './config';
import { Backend } from './backend';
import { AUTH_STORAGE_FLAGS, SessionManager, SHOPIFY_LOCAL_STORAGE_KEYS } from './session-manager';
import { Checkout } from './checkout';
import { AppState } from '../etc/stores';
import { Auth } from './auth';
import flagKeys from '../etc/flagKeys';
import { parseMoney } from '../etc/utils';
import { WidgetConnect } from './widget-connect';
import sessionKeys from '../etc/sessionKeys';
const SHOPIFY_CONNECTING_ACCOUNT = 'shopify_connecting_account';
export class Shopify extends Service {
    constructor() {
        super(...arguments);
        this.config = this.ctx.get(Config);
        this.checkout = this.ctx.get(Checkout);
        this.backend = this.ctx.get(Backend);
        this.session = this.ctx.get(SessionManager);
        this.auth = this.ctx.get(Auth);
        this.connect = this.ctx.get(WidgetConnect);
    }
    getAuthState() {
        return __awaiter(this, void 0, void 0, function* () {
            const userId = this.getCurrentCustomerId();
            const { account } = this.config.getSiteSettings();
            let token = null;
            if (userId) {
                const localToken = this.session.get(sessionKeys.TOKEN);
                const response = localToken ||
                    (yield this.backend.customerConnectAuthenticate({
                        customerId: userId
                    }));
                token = response.token;
            }
            if (token) {
                this.session.set(sessionKeys.TOKEN, { token });
            }
            else {
                this.session.remove(sessionKeys.TOKEN);
                AppState.update(state => (Object.assign(Object.assign({}, state), { auth: Object.assign(Object.assign({}, state.auth), { connected: false, access_token: undefined }) })));
            }
            return {
                token,
                userId: userId === null || userId === void 0 ? void 0 : userId.toString(),
                provider: 'shopify',
                account
            };
        });
    }
    isCurrentCartPage() {
        return window.location.href.includes('/cart');
    }
    isCurrentRemindCodePage() {
        return this.isCurrentCartPage();
    }
    applyCoupon(couponCode) {
        return __awaiter(this, void 0, void 0, function* () {
            yield axios.get(`discount/${couponCode}`);
        });
    }
    getExtensionStorageCheckout() {
        const extensionStorage = this.session.getRaw(SHOPIFY_LOCAL_STORAGE_KEYS.EXTENSION_STORAGE);
        if (!extensionStorage)
            return null;
        try {
            const parsedExtensionStorage = JSON.parse(extensionStorage);
            const [_, value] = Object.entries(parsedExtensionStorage).find(([key]) => key.includes('twism__checkout'));
            return value;
        }
        catch (e) {
            return null;
        }
    }
    removeTwismCheckoutFromExtensionStorage() {
        const extensionStorage = this.session.getRaw(SHOPIFY_LOCAL_STORAGE_KEYS.EXTENSION_STORAGE);
        if (!extensionStorage)
            return;
        try {
            const parsedExtensionStorage = JSON.parse(extensionStorage);
            const keysToRemove = Object.keys(parsedExtensionStorage).filter(key => key.includes('twism__checkout'));
            keysToRemove.forEach(key => {
                delete parsedExtensionStorage[key];
            });
            this.session.setRaw(SHOPIFY_LOCAL_STORAGE_KEYS.EXTENSION_STORAGE, JSON.stringify(parsedExtensionStorage), { saveOn: 'localStorage' });
        }
        catch (e) {
            return;
        }
    }
    hasPurchaseOccurred() {
        const extensionStorageCheckout = this.getExtensionStorageCheckout();
        return !!(extensionStorageCheckout === null || extensionStorageCheckout === void 0 ? void 0 : extensionStorageCheckout.orderId);
    }
    registerWebsiteEvents() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { account } = this.config.getSiteSettings();
            const currentPathName = window['location'].pathname;
            if (this.isCurrentRemindCodePage()) {
                const checkoutValue = Number((document.querySelector('.totals__subtotal-value') ||
                    document.querySelector('.cart-subtotal__price') || {
                    textContent: '0'
                }).textContent.replace(/[^0-9.-]+/g, ''));
                this.session.set(sessionKeys.CHECKOUT_VALUE, checkoutValue, {
                    expires: Time.addDays(new Date(), 1)
                });
            }
            if (this.hasPurchaseOccurred()) {
                const { orderId, amount, coinsAmount } = this.getExtensionStorageCheckout();
                yield this.auth.loadState();
                AppState.update(state => (Object.assign(Object.assign({}, state), { customer: Object.assign({}, state.customer) })));
                yield this.checkout.handleCheckout({
                    externalTransactionId: orderId.toString(),
                    transactionId: orderId.toString(),
                    account,
                    provider: 'shopify',
                    value: amount * 100,
                    coinsAmount: coinsAmount * 100,
                    currency: (_a = window['Shopify'].currency) === null || _a === void 0 ? void 0 : _a.active
                }, 'shopify');
                this.removeTwismCheckoutFromExtensionStorage();
            }
            if (this.getCurrentCustomerId() &&
                this.session.get(SHOPIFY_CONNECTING_ACCOUNT)) {
                this.session.remove(SHOPIFY_CONNECTING_ACCOUNT);
                const getData = () => __awaiter(this, void 0, void 0, function* () {
                    if (this.session.get(AUTH_STORAGE_FLAGS.referral)) {
                        const searchParams = new URLSearchParams(this.session.get(AUTH_STORAGE_FLAGS.referral));
                        const campaign = searchParams.get('twism-campaign') || searchParams.get('campaign');
                        const linkId = searchParams.get('twism-linkId') || searchParams.get('LinkId');
                        if (campaign === 'business-referral' && linkId) {
                            const referralReferredState = yield this.backend.getReferralByLinkId(linkId);
                            AppState.update(state => (Object.assign(Object.assign({}, state), { referralReferredState })));
                        }
                        return {
                            category: 'referredCoin',
                            authFlow: 'referred-redeem'
                        };
                    }
                    if (this.session.get(AUTH_STORAGE_FLAGS.welcomeCoins)) {
                        return {
                            category: 'welcomeCoinAvailable',
                            authFlow: 'free-coins'
                        };
                    }
                    if (this.session.get(AUTH_STORAGE_FLAGS.customerImport)) {
                        return {
                            authFlow: 'customer-import',
                            popoverOpen: true,
                            popoverRouteOnOpen: {
                                route: '/customer-import-welcome'
                            }
                        };
                    }
                    if (this.session.get(AUTH_STORAGE_FLAGS.customerAcquisition)) {
                        return {
                            authFlow: 'customer-acquisition',
                            popoverOpen: true,
                            popoverRouteOnOpen: {
                                route: '/customer-acquisition'
                            }
                        };
                    }
                    if (this.session.get(AUTH_STORAGE_FLAGS.fastRedemption)) {
                        return {
                            category: 'fastRedemption',
                            authFlow: 'fast-redemption'
                        };
                    }
                    if (this.session.get(sessionKeys.CHECKOUT)) {
                        const { coinsAmount, moneyAmount } = this.session.get(sessionKeys.CHECKOUT);
                        return {
                            category: 'youGotCoins',
                            checkout: true,
                            popoverOpen: false,
                            coins: parseMoney(coinsAmount),
                            spent: parseMoney(moneyAmount),
                            requestEmailVerification: false
                        };
                    }
                    return {
                        popoverOpen: true
                    };
                });
                const flow = yield getData();
                AppState.update(state => (Object.assign(Object.assign({}, state), { website: Object.assign(Object.assign(Object.assign({}, state.website), flow), { connectingAccount: true }) })));
            }
            if (currentPathName.includes('account/activate')) {
                this.session.set(SHOPIFY_CONNECTING_ACCOUNT, true);
            }
        });
    }
    openLoginModal(data) {
        this.session.set(SHOPIFY_CONNECTING_ACCOUNT, true, {
            expires: Time.addHours(new Date(), 1)
        });
        if (data && data.mode == 'login') {
            window.location.href = '/account/login';
        }
        else {
            window.location.href = '/account/register';
        }
    }
    isConnectingAccount() {
        return this.session.get(SHOPIFY_CONNECTING_ACCOUNT);
    }
    isSoftLoginEnabled() {
        return __awaiter(this, void 0, void 0, function* () {
            const { items: featureFlags } = yield this.backend.getFeatureFlags();
            const flagEnabled = featureFlags.includes(flagKeys.NEW_SIGNUP_WIDGET_SHOPIFY_MODAL);
            const { account } = yield this.getAuthState();
            const scopes = yield this.connect.getScopes(account);
            return scopes.includes('write_customers') && flagEnabled;
        });
    }
    getCurrentCustomerId() {
        var _a, _b, _c;
        const shopifyDOM = window['ShopifyAnalytics'];
        const customerId = (_b = (_a = shopifyDOM === null || shopifyDOM === void 0 ? void 0 : shopifyDOM.meta) === null || _a === void 0 ? void 0 : _a.page) === null || _b === void 0 ? void 0 : _b.customerId;
        if (customerId) {
            this.session.set(sessionKeys.CUSTOMER, { customerId });
        }
        return (_c = this.session.get(sessionKeys.CUSTOMER)) === null || _c === void 0 ? void 0 : _c.customerId;
    }
}
Container.add(Shopify);
